import { ChevronDownMedium, ChevronUpMedium } from '@zoopla-eng/zds/icons';
import { Select } from '@zoopla-eng/zds/select';
import { fontGroups } from '@zoopla-eng/zds/themes';
import { buildSelectBedsTitle } from '@zoopla/redux-reducer-filters';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { DropdownContainer } from '../DropdownContainer';
import { onDocumentClick } from '../utils/onDocumentClick';
import {
    selectBedToggleArrow,
    selectBedToggleButton,
    selectBedToggleLabel,
    selectBedToggleText,
    selectBedWrapperStyle
} from './SelectBeds.styles.css';

const translationsMap = new Map([
    ['studio', 'studio'],
    ['studios', 'studios'],
    ['bed', 'bed'],
    ['beds', 'beds'],
    ['min', 'min'],
    ['max', 'max'],
    ['default', 'Any beds']
]);

const beds = ['', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

const bedsOptions = [
    { value: '0', text: 'Studio' },
    { value: '1', text: '1' },
    { value: '2', text: '2' },
    { value: '3', text: '3' },
    { value: '4', text: '4' },
    { value: '5', text: '5' },
    { value: '6', text: '6' },
    { value: '7', text: '7' },
    { value: '8', text: '8' },
    { value: '9', text: '9' },
    { value: '10', text: '10+' }
];

export type BedsMin = typeof beds[number];
export type BedsMax = typeof beds[number];

type SelectBedsProps = {
    value: {
        bedsMin: BedsMin;
        bedsMax: BedsMax;
    };
    onChange: ({
        bedsMin,
        bedsMax
    }: {
        bedsMin: BedsMin;
        bedsMax: BedsMax;
    }) => void;
};

export const SelectBeds = ({ value, onChange }: SelectBedsProps) => {
    const [isActive, setIsActive] = useState(false);
    const rootNode = useRef() as MutableRefObject<HTMLDivElement>;

    const toggle = () => {
        setIsActive(!isActive);
    };

    const dropdownTitle = buildSelectBedsTitle(
        parseInt(value.bedsMin, 10),
        parseInt(value.bedsMax, 10),
        translationsMap
    );

    const bedsMinOptions =
        value.bedsMax !== ''
            ? bedsOptions.slice(0, Number(value.bedsMax) + 1)
            : bedsOptions;

    const bedsMaxOptions =
        value.bedsMin !== ''
            ? bedsOptions.slice(Number(value.bedsMin))
            : bedsOptions;

    const minBedOptions = bedsMinOptions.map((bedsMinOption, index) => ({
        id: bedsMinOption.value,
        text: bedsMinOption.text
    }));

    const maxBedOptions = bedsMaxOptions.map((bedsMaxOption, index) => ({
        id: bedsMaxOption.value,
        text: bedsMaxOption.text
    }));

    const minDefaultOption = {
        id: '',
        text: 'No min'
    };

    const maxDefaultOption = {
        id: '',
        text: 'No max'
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(
        onDocumentClick((evt: globalThis.MouseEvent) => {
            if (
                rootNode.current &&
                rootNode.current.contains(evt.target as Node)
            ) {
                return;
            }

            setIsActive(false);
        }),
        []
    );

    return (
        <div
            className={selectBedWrapperStyle}
            data-testid="AnyBeds_testId"
            ref={rootNode}
        >
            <button
                className={`${selectBedToggleButton} ${fontGroups.ladle}`}
                onClick={toggle}
            >
                <label className={selectBedToggleLabel}>Bedrooms</label>
                <span className={selectBedToggleText}>{dropdownTitle}</span>
                <div className={selectBedToggleArrow} aria-hidden>
                    {isActive ? <ChevronUpMedium /> : <ChevronDownMedium />}
                </div>
            </button>
            <DropdownContainer isActive={isActive}>
                <Select
                    options={[minDefaultOption, ...minBedOptions]}
                    onSelectedOptionChange={(o) => {
                        onChange({
                            ...value,
                            bedsMin: o.id
                        });
                    }}
                    selectedOption={
                        minBedOptions.find((o) => o.id === value.bedsMin) ??
                        minDefaultOption
                    }
                    label="Minimum beds"
                    data-testid="min_beds_testid"
                />
                <Select
                    options={[maxDefaultOption, ...maxBedOptions]}
                    onSelectedOptionChange={(o) => {
                        onChange({
                            ...value,
                            bedsMax: o.id
                        });
                    }}
                    selectedOption={
                        maxBedOptions.find((o) => o.id === value.bedsMax) ??
                        maxDefaultOption
                    }
                    label="Maximum beds"
                    data-testid="max_beds_testid"
                />
            </DropdownContainer>
        </div>
    );
};
